import React, { useState, useRef } from "react";
import {
  Typography,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Box,
  Chip,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, FieldArray } from "formik";
import Signature from "../../../../common/signature/signature";
import { Delete } from "@mui/icons-material";
import ReusableDialog from "../../../../common/dialog/dialog";
import * as Yup from "yup";

const homeNames = ["Others", "Male", "Female"]; // Gender options

// Define validation schema for PEEP
const validationSchema = Yup.object().shape({
  individualName: Yup.string().required("Individual name is required"),
  peepCompleter: Yup.string().required("Person completing PEEP is required"),
  homeName: Yup.string().required("Home Name is required"),
  address: Yup.string().required("Address is required"),
  supportRequirement: Yup.string().required("Support requirement is required"),
  assistanceDetails: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Name is required"),
      assistanceType: Yup.string().required("Nature of assistance is required"),
      contactDetails: Yup.string().required("Contact details are required"),
    })
  ),
  trainingDetails: Yup.string().required("Training details are required"),
  equipmentDetails: Yup.object().shape({
    location: Yup.string().required("Equipment location is required"),
    equipmentName: Yup.string().required("Equipment Name is required"),
    responsiblePerson: Yup.string().required("Responsible person is required"),
    availability: Yup.string().required("Availability details are required"),
  }),
  evacuationProcedure: Yup.string().required("Evacuation procedure is required"),
  communicationDetails: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required("Name is required"),
      date: Yup.string().required("Date is required"),
      signature: Yup.string().required("Signature is required"),
    })
  ),
});

const PersonalEmergencyAdmission = () => {
  const signatureRef = useRef(null);
  const signatureRefLeft = useRef(null);
  const signatureRefRight = useRef(null);
  const [openAddAssistanceDialog, setOpenAddAssistanceDialog] = useState(false);
  const [openAddCommunicationDialog, setOpenAddCommunicationDialog] = useState(false);

  const [newAssistance, setNewAssistance] = useState({
    name: "",
    assistanceType: "",
    contactDetails: "",
    toggle: "no", // Add toggle property here
  });

  const [newCommunication, setNewCommunication] = useState({
    name: "",
    date: "",
    signature: "",
  });

  const handleAssistanceChange = (e) => {
    const { name, value } = e.target;
    setNewAssistance((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddAssistance = (push) => {
    push(newAssistance);
    setOpenAddAssistanceDialog(false);
    setNewAssistance({ name: "", assistanceType: "", contactDetails: "" });
  };

  const handleCommunicationChange = (e) => {
    const { name, value } = e.target;
    setNewCommunication((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddCommunication = (push) => {
    const signatureData = signatureRef.current?.getSignatureData();
    if (!signatureData) {
      alert("Please provide a signature");
      return;
    }

    // Push the new communication record to Formik's `communicationDetails` field
    push({
      ...newCommunication,
      signature: signatureData,
    });

    // Reset dialog state and close dialog
    setNewCommunication({ name: "", date: "", signature: "" });
    setOpenAddCommunicationDialog(false);
  };

  const handleToggle = (event, newToggle) => {
    setNewAssistance((prevState) => ({
      ...prevState,
      toggle: newToggle, // Update toggle within newAssistance
    }));
  };

  // Check if all fields are filled
  const isAddButtonDisabled = !(newCommunication.name && newCommunication.date);
  // Check if all fields are filled
  const isAssistButtonDisabled = !(newAssistance.name && newAssistance.assistanceType && newAssistance.contactDetails);

  return (
    <Formik
      initialValues={{
        individualName: "",
        peepCompleter: "",
        homeName: "",
        address: "",
        supportRequirement: "",
        assistanceDetails: [],
        trainingDetails: "",
        equipmentDetails: {
          equipmentName: "",
          location: "",
          responsiblePerson: "",
          availability: "",
        },
        evacuationProcedure: "",
        communicationDetails: [],
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const signatureDataLeft = signatureRefLeft.current?.getSignatureData();
        const signatureDataRight = signatureRefRight.current?.getSignatureData();

        if (!signatureDataLeft || !signatureDataRight) {
          alert("Please provide a signature");
          return;
        }

        const formData = {
          ...values,
          signatureDataPerson: signatureDataLeft,
          signatureDataManager: signatureDataRight,
        };
        console.log("Form data submitted:", formData);
      }}
    >
      {({ values, setFieldValue, touched, errors, handleBlur, handleChange }) => (
        <Form className='admission-form'>
          <Box mb={2}>
            <Chip label='Admission' />
            <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
              Personal Emergency Evacuation Plan (PEEP) HSF90
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
            A PEEP should be developed when an service user requires specific arrangements to bein place to support
            their evacuation in an emergency. This plan should be developed in conjunction with the person concerned.
            Where this PEEP and its arrangements cannot be implemented,(e.g. Due to building worker breakdown of
            equipment), alternative working arrangements or accommodation for the individual must be considered.
          </Typography>
          <Grid container mt={2}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }}>Individual requiring support</Typography>
              <TextField
                fullWidth
                name='individualName'
                placeholder='Enter Name of the person'
                value={values.individualName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.individualName && Boolean(errors.individualName)}
                helperText={touched.individualName && errors.individualName}
              />
            </Grid>
          </Grid>

          <Grid container mt={1}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }}>Person completing the PEEP</Typography>
              <TextField
                fullWidth
                name='peepCompleter'
                value={values.peepCompleter}
                onBlur={handleBlur}
                error={touched.peepCompleter && Boolean(errors.peepCompleter)}
                helperText={touched.peepCompleter && errors.peepCompleter}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id='homeName'
                options={homeNames}
                value={values.homeName}
                onChange={(event, newValue) => setFieldValue("homeName", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Home Name
                    </Typography>
                    <TextField
                      {...params}
                      placeholder='Home Name'
                      error={Boolean(touched.homeName && errors.homeName)}
                      helperText={touched.homeName && errors.homeName ? errors.homeName : ""}
                    />
                  </>
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Address
              </Typography>
              <TextField
                fullWidth
                name='address'
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.address && Boolean(errors.address)}
                helperText={touched.address && errors.address}
              />
            </Grid>
          </Grid>

          <Grid container mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>
              Why does the individual require support in an emergency evacuation?
            </Typography>
            <Typography sx={{ fontSize: "14px" }} align='left'>
              How does the individual’s health, mobility, hearing, sight, communication etc. impact on their safe
              evacuation?
            </Typography>
            <TextField
              fullWidth
              name='supportRequirement'
              multiline
              rows={4}
              value={values.supportRequirement}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.supportRequirement && Boolean(errors.supportRequirement)}
              helperText={touched.supportRequirement && errors.supportRequirement}
            />
          </Grid>

          <Grid mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>What assistance is required?</Typography>
            <Typography sx={{ fontSize: "14px" }} align='left'>
              Detail the type of support the individual will need to evacuate safely.
            </Typography>
            <TextField
              disabled
              fullWidth
              multiline
              rows={4}
              label={
                <Typography sx={{ fontSize: "14px" }}>
                  <ul>
                    <li>Will they need specific arrangements to alert them of an emergency?</li>
                    <li>Will they need people or equipment to assist their evacuation?</li>
                    <li>Will they require to evacuate using a specific route?</li>
                  </ul>
                </Typography>
              }
            />
          </Grid>

          <Box mt={4}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Who will provide support?</Typography>
            <Typography sx={{ fontSize: "14px" }} align='left' mb={2}>
              Identify those people who will provide assistance and the nature of the assistance. (You should identify
              who will provide back-up cover during absence e.g. holiday, sickness etc.) 
            </Typography>
            <FieldArray name='assistanceDetails'>
              {({ remove, push }) => (
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Name
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Training
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Nature of Assistance
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Contact Details
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.assistanceDetails.map((assist, index) => (
                          <TableRow key={index}>
                            <TableCell>{assist.name}</TableCell>
                            <TableCell>{assist.toggle}</TableCell>
                            <TableCell>{assist.assistanceType}</TableCell>
                            <TableCell>{assist.contactDetails}</TableCell>
                            <TableCell align='right'>
                              <IconButton color='primary' onClick={() => remove(index)}>
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    sx={{ fontSize: "14px", color: "#1751D0" }}
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddAssistanceDialog(true)}
                  >
                    Add another record
                  </Button>

                  <ReusableDialog
                    isOpen={openAddAssistanceDialog}
                    onClose={() => setOpenAddAssistanceDialog(false)}
                    title='Add Support Provider ?'
                    contentText={
                      "Add the relavent details to the fields to add a another perosn to the involved parties."
                    }
                    onSubmit={() => handleAddAssistance(push)}
                    submitText='Proceed'
                    cancelText='Cancel'
                    disable={isAssistButtonDisabled}
                  >
                    <Box mt={2}>
                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                        Name
                      </Typography>
                      <TextField
                        fullWidth
                        name='name'
                        placeholder='Enter Name of the Person'
                        value={newAssistance.name}
                        onChange={handleAssistanceChange}
                      />

                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left' mt={1}>
                        What Training?
                      </Typography>

                      <Grid container alignItems='center' mb={1}>
                        <ToggleButtonGroup
                          color='primary'
                          value={newAssistance.toggle} // Use newAssistance.toggle here
                          exclusive
                          onChange={handleToggle} // Call handleToggle to update toggle state
                          aria-label='yes no toggle'
                        >
                          <ToggleButton value='yes' aria-label='Yes'>
                            Yes
                          </ToggleButton>
                          <ToggleButton value='no' aria-label='No'>
                            No
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>

                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left' mt={1}>
                        Nature of Assistance
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder='Enter Nature of Assistance'
                        name='assistanceType'
                        value={newAssistance.assistanceType}
                        onChange={handleAssistanceChange}
                      />
                      <Typography sx={{ fontSize: "14px", color: "black" }} align='left' mt={1}>
                        Contact Details
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder='Enter Contact Details'
                        name='contactDetails'
                        value={newAssistance.contactDetails}
                        onChange={handleAssistanceChange}
                      />
                    </Box>
                  </ReusableDialog>
                </>
              )}
            </FieldArray>
          </Box>
          <Box mt={4}>
            <Typography sx={{ color: "black", fontSize: "14px" }}>Equipment</Typography>
            <Typography sx={{ fontSize: "14px" }} mt={2}>
              What equipment will be provided to assist with the evacuation?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              name='equipmentDetails.equipmentName'
              value={values.equipmentDetails.equipmentName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.equipmentDetails?.equipmentName && Boolean(errors.equipmentDetails?.equipmentName)}
              helperText={touched.equipmentDetails?.equipmentName && errors.equipmentDetails?.equipmentName}
            />

            <Typography sx={{ fontSize: "14px" }} mt={2}>
              Where is this located?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              name='equipmentDetails.location'
              value={values.equipmentDetails.location}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.equipmentDetails?.location && Boolean(errors.equipmentDetails?.location)}
              helperText={touched.equipmentDetails?.location && errors.equipmentDetails?.location}
            />
            <Typography sx={{ fontSize: "14px" }} mt={2}>
              Who is responsible for maintaining this?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              name='equipmentDetails.responsiblePerson'
              value={values.equipmentDetails.responsiblePerson}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.equipmentDetails?.responsiblePerson && Boolean(errors.equipmentDetails?.responsiblePerson)}
              helperText={touched.equipmentDetails?.responsiblePerson && errors.equipmentDetails?.responsiblePerson}
            />
            <Typography sx={{ fontSize: "14px" }} mt={2}>
              Is equipment available for everyone within a suitable time frame?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              name='equipmentDetails.availability'
              value={values.equipmentDetails.availability}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.equipmentDetails?.availability && Boolean(errors.equipmentDetails?.availability)}
              helperText={touched.equipmentDetails?.availability && errors.equipmentDetails?.availability}
            />
          </Box>

          <Box mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Evacuation procedure and route</Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Detail the evacuation procedure including safe routes to be taken. (Have alternative routes been
              identified should the first route be unavailable?).
            </Typography>
            <TextField
              fullWidth
              name='evacuationProcedure'
              multiline
              rows={3}
              value={values.evacuationProcedure}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.evacuationProcedure && Boolean(errors.evacuationProcedure)}
              helperText={touched.evacuationProcedure && errors.evacuationProcedure}
            />
          </Box>

          <Typography sx={{ fontSize: "14px", color: "black" }} mt={5}>
            Agreement of the PEEP Arrangements
          </Typography>

          {/* Signature */}

          <Grid container spacing={2} mt={1}>
            <Grid item>
              <Box>
                <Signature ref={signatureRefLeft} />
                <Typography align='center'>
                  <Grid container ml={8}>
                    <Grid>
                      <Typography sx={{ fontSize: "14px", color: "#FFB700" }}>Venorox </Typography>
                    </Grid>
                    <Grid>
                      <Grid>
                        <Typography sx={{ fontSize: "14px", color: "black" }}>/ Signature</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Typography>2024/03/12</Typography>

                  <Grid className='clear-signature-button'>
                    <Button variant='outlined' onClick={() => signatureRefLeft.current?.clearSignature()}>
                      Clear
                    </Button>
                  </Grid>
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box>
                <Signature ref={signatureRefRight} />
                <Typography align='center'>
                  <Grid container ml={8}>
                    <Grid>
                      <Typography sx={{ fontSize: "14px", color: "#FFB700" }}>Venorox </Typography>
                    </Grid>
                    <Grid>
                      <Grid>
                        <Typography sx={{ fontSize: "14px", color: "black" }}>/ Signature</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Typography>2024/03/12</Typography>

                  <Grid className='clear-signature-button'>
                    <Button variant='outlined' onClick={() => signatureRefRight.current?.clearSignature()}>
                      Clear
                    </Button>
                  </Grid>
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box mt={3}>
            <Typography sx={{ fontSize: "14px", color: "black" }}>Communication of the PEEP</Typography>
            <Typography sx={{ fontSize: "14px" }} mb={2}>
              Communication of the PEEP(to those who are supporting the individual)Ihave read the PEEP and fully
              understand my role in the event of evacuation in peep document
            </Typography>
            <FieldArray name='communicationDetails'>
              {({ remove, push }) => (
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Name
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Date
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Signature
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", backgroundColor: "#E3F2FD", fontSize: "12px", color: "#1751D0" }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.communicationDetails.map((comm, index) => (
                          <TableRow key={index}>
                            <TableCell>{comm.name}</TableCell>
                            <TableCell>{comm.date}</TableCell>
                            <TableCell>
                              <img src={comm.signature} alt='Signature' style={{ width: 100 }} />
                            </TableCell>
                            <TableCell align='right'>
                              <IconButton color='primary' onClick={() => remove(index)}>
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    sx={{ fontSize: "14px", color: "#1751D0" }}
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddCommunicationDialog(true)}
                  >
                    Add another record
                  </Button>

                  <ReusableDialog
                    isOpen={openAddCommunicationDialog}
                    onClose={() => setOpenAddCommunicationDialog(false)}
                    title='Add Communication Record'
                    contentText='Add the relevant details to the fields to add another person to the involved parties.'
                    onSubmit={() => handleAddCommunication(push)}
                    submitText='Proceed'
                    cancelText='Cancel'
                    disable={isAddButtonDisabled}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontSize: "14px", color: "black" }} mt={2}>
                          Name
                        </Typography>
                        <TextField
                          fullWidth
                          name='name'
                          value={newCommunication.name}
                          onChange={handleCommunicationChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontSize: "14px", color: "black" }} mt={2}>
                          Date
                        </Typography>
                        <TextField
                          fullWidth
                          name='date'
                          type='date'
                          value={newCommunication.date}
                          onChange={handleCommunicationChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>

                    {/* Signature */}
                    <Grid item mt={2}>
                      <Box>
                        <Signature ref={signatureRef} />
                        <Typography align='right'>
                          <Grid container>
                            <Grid>
                              <Grid container>
                                <Grid>
                                  <Typography sx={{ fontSize: "14px", color: "#FFB700" }}>Venorox </Typography>
                                </Grid>
                                <Grid>
                                  <Grid>
                                    <Typography sx={{ fontSize: "14px" }}>/ Signature</Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Typography>{newCommunication.date}</Typography>
                            </Grid>
                          </Grid>
                          <Grid container justifyContent='space-between' alignItems='center'>
                            <Button variant='outlined' onClick={() => signatureRef.current?.clearSignature()}>
                              Clear
                            </Button>
                          </Grid>
                        </Typography>
                      </Box>
                    </Grid>
                  </ReusableDialog>
                </>
              )}
            </FieldArray>
          </Box>

          {/* Submit button */}
          <Grid item xs={12} mt={2}>
            <div className='submit-button-container'>
              <Button type='submit' variant='contained' color='primary' className='submit-button'>
                Submit
              </Button>
            </div>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default PersonalEmergencyAdmission;
